import React from 'react';
import { MapPin, Pencil, AlertTriangle } from 'lucide-react';
import OptimizedInput from '../OptimizedInput';
import LocationVisualization from '../../LocationVisualization';
import LocationSearchBox from './LocationSearchBox';
import { locationUtils } from '../../../utils/location';

const inputCls = 'w-full border border-ink-faint rounded-sm px-3.5 py-2.5 text-sm bg-surface focus:ring-1 focus:ring-accent focus:border-accent';
const labelCls = 'dpc-label text-ink-faint block mb-1.5';
const errorCls = 'text-xs text-accent mt-1';

// Shared by both entry modes so the address/city/country/county fields exist
// in exactly one place in the code, even though either mode can show them.
const AddressFields = ({ checkoutFormData, inputHandlers, handleLocationChange, formErrors }) => (
  <div className="space-y-4">
    <div>
      <label className={labelCls}>Address</label>
      <OptimizedInput value={checkoutFormData.address} onChange={inputHandlers.address} className={inputCls} />
      {formErrors.address && <p className={errorCls}>{formErrors.address}</p>}
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      <div>
        <label className={labelCls}>City</label>
        <OptimizedInput value={checkoutFormData.city} onChange={inputHandlers.city} className={inputCls} />
        {formErrors.city && <p className={errorCls}>{formErrors.city}</p>}
      </div>
      <div>
        <label className={labelCls}>Country</label>
        <select
          value={checkoutFormData.country}
          onChange={(e) => handleLocationChange('country', e.target.value)}
          className={inputCls}
        >
          {locationUtils.countries.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      {checkoutFormData.country === 'Kenya' && (
        <div>
          <label className={labelCls}>County</label>
          <select
            value={checkoutFormData.county}
            onChange={(e) => handleLocationChange('county', e.target.value)}
            className={inputCls}
          >
            <option value="">Select county</option>
            {locationUtils.counties.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      )}
    </div>
  </div>
);

const ReviewStep = ({
  cartItems,
  checkoutTotals,
  checkoutFormData,
  inputHandlers,
  formErrors,
  handleLocationChange,
  locationEntryMode,
  setLocationEntryMode,
  manualMapRef,
  applySearchResultRef,
  locationPrecisionWarning,
  onContinue,
}) => (
  <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 p-6 sm:p-8">
    {/* Order summary */}
    <div className="lg:col-span-2">
      <h3 className="font-display text-xl text-ink mb-5">Order summary</h3>
      <ul className="space-y-4 mb-6">
        {cartItems.map((item) => (
          <li key={item.cartLineId} className="flex gap-3">
            <div className="w-14 h-16 bg-paper rounded-sm overflow-hidden flex-shrink-0">
              <img src={item.imageUrl || item.image} alt={item.name} className="w-full h-full object-cover" onError={(e) => { e.target.src = '/images/placeholder.jpg'; }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-ink truncate">{item.name}</p>
              {item.selectedColor && <p className="text-xs text-ink-faint">{item.colorName || item.selectedColor} · Qty {item.quantity}</p>}
            </div>
            <span className="text-sm text-ink">KSh {(item.price * item.quantity).toLocaleString()}</span>
          </li>
        ))}
      </ul>
      <div className="space-y-2 pt-4 border-t border-line text-sm">
        <div className="flex justify-between"><span className="text-ink-soft">Subtotal</span><span className="text-ink">KSh {checkoutTotals.subtotal.toLocaleString()}</span></div>
        <div className="flex justify-between">
          <span className="text-ink-soft">Shipping{checkoutFormData.county ? ` to ${checkoutFormData.county}` : ''}</span>
          <span className="text-ink">{checkoutTotals.shipping === 0 ? 'Free' : `KSh ${checkoutTotals.shipping.toLocaleString()}`}</span>
        </div>
        {checkoutTotals.costs.map((cost) => (
          <div key={cost.id || cost.name} className="flex justify-between">
            <span className="text-ink-soft">{cost.name}</span>
            <span className="text-ink">KSh {cost.calculatedAmount.toLocaleString()}</span>
          </div>
        ))}
        <div className="flex justify-between pt-2 border-t border-line font-display text-lg text-ink">
          <span>Total</span><span>KSh {checkoutTotals.total.toLocaleString()}</span>
        </div>
      </div>
    </div>

    {/* Customer info */}
    <div className="lg:col-span-3">
      <h3 className="font-display text-xl text-ink mb-5">Delivery details</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label className={labelCls}>Full name</label>
          <OptimizedInput value={checkoutFormData.fullName} onChange={inputHandlers.fullName} className={inputCls} />
          {formErrors.fullName && <p className={errorCls}>{formErrors.fullName}</p>}
        </div>
        <div>
          <label className={labelCls}>Email</label>
          <OptimizedInput type="email" value={checkoutFormData.email} onChange={inputHandlers.email} className={inputCls} />
          {formErrors.email && <p className={errorCls}>{formErrors.email}</p>}
        </div>
        <div>
          <label className={labelCls}>Phone</label>
          <OptimizedInput type="tel" value={checkoutFormData.phone} onChange={inputHandlers.phone} className={inputCls} />
          {formErrors.phone && <p className={errorCls}>{formErrors.phone}</p>}
        </div>
        <div>
          <label className={labelCls}>Company (optional)</label>
          <OptimizedInput value={checkoutFormData.company} onChange={inputHandlers.company} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Date of birth (optional)</label>
          <OptimizedInput type="date" value={checkoutFormData.dateOfBirth} onChange={inputHandlers.dateOfBirth} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Gender (optional)</label>
          <select
            value={checkoutFormData.gender}
            onChange={(e) => inputHandlers.gender(e.target.value)}
            className={inputCls}
          >
            <option value="">Prefer not to say</option>
            <option value="female">Female</option>
            <option value="male">Male</option>
            <option value="other">Other</option>
          </select>
        </div>
      </div>

      {/* Delivery location — map and manual entry are mutually exclusive,
          so address/city/county are only ever asked for once. */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <label className={labelCls}>Delivery location</label>
          <button
            type="button"
            onClick={() => setLocationEntryMode((m) => (m === 'map' ? 'manual' : 'map'))}
            className="flex items-center gap-1.5 text-xs text-accent hover:text-accent-deep dpc-label"
          >
            {locationEntryMode === 'map' ? (
              <><Pencil className="w-3.5 h-3.5" /> Type my address instead</>
            ) : (
              <><MapPin className="w-3.5 h-3.5" /> Pin it on the map instead</>
            )}
          </button>
        </div>

        {locationEntryMode === 'map' ? (
          <>
            <LocationSearchBox
              onSelect={(result) => applySearchResultRef.current?.(result.lat, result.lng, result.radiusMeters, result.isWideArea, result.placeName)}
            />
            {locationPrecisionWarning && (
              <div className="flex items-start gap-2 bg-accent-soft border border-accent/30 rounded-sm px-3.5 py-2.5 mb-3 text-xs text-accent-deep">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <p>{locationPrecisionWarning}</p>
              </div>
            )}
            <div ref={manualMapRef} className="w-full h-64 rounded-md mb-3 bg-paper border border-line" />
            {!checkoutFormData.coordinates ? (
              <p className="text-xs text-ink-faint">Click or tap the map to drop a pin at your delivery address — we'll fill in the details below.</p>
            ) : (
              <>
                <p className="text-xs text-ink-soft mb-3">Detected from your pin — edit anything that's not quite right.</p>
                <AddressFields
                  checkoutFormData={checkoutFormData}
                  inputHandlers={inputHandlers}
                  handleLocationChange={handleLocationChange}
                  formErrors={formErrors}
                />
              </>
            )}
          </>
        ) : (
          <AddressFields
            checkoutFormData={checkoutFormData}
            inputHandlers={inputHandlers}
            handleLocationChange={handleLocationChange}
            formErrors={formErrors}
          />
        )}
      </div>

      {locationEntryMode === 'map' && (checkoutFormData.coordinates || checkoutFormData.locationData) && (
        <div className="mb-4">
          <LocationVisualization
            locationData={checkoutFormData.locationData}
            coordinates={checkoutFormData.coordinates}
            addressData={{
              address: checkoutFormData.address,
              city: checkoutFormData.city,
              county: checkoutFormData.county,
              country: checkoutFormData.country,
              postalCode: checkoutFormData.postalCode,
            }}
            compact
            view="card"
          />
        </div>
      )}

      <div className="mb-4">
        <label className={labelCls}>Special instructions (optional)</label>
        <textarea
          rows={2}
          value={checkoutFormData.specialInstructions}
          onChange={(e) => inputHandlers.specialInstructions(e.target.value)}
          className={`${inputCls} resize-none`}
        />
      </div>
      <div className="mb-5">
        <label className={labelCls}>Delivery notes (optional)</label>
        <textarea
          rows={2}
          value={checkoutFormData.deliveryNotes}
          onChange={(e) => inputHandlers.deliveryNotes(e.target.value)}
          className={`${inputCls} resize-none`}
        />
      </div>

      <div className="space-y-2 mb-6">
        <label className="flex items-center gap-2 text-sm text-ink-soft">
          <input type="checkbox" checked={Boolean(checkoutFormData.marketingConsent)} onChange={(e) => inputHandlers.marketingConsent(e.target.checked)} className="accent-accent" />
          Send me new-arrival updates by email
        </label>
        <label className="flex items-center gap-2 text-sm text-ink-soft">
          <input type="checkbox" checked={Boolean(checkoutFormData.smsConsent)} onChange={(e) => inputHandlers.smsConsent(e.target.checked)} className="accent-accent" />
          Send me delivery updates by SMS
        </label>
      </div>

      <button
        onClick={onContinue}
        className="w-full sm:w-auto bg-ink text-paper dpc-label px-8 py-3.5 rounded-full hover:bg-accent transition-colors"
      >
        Continue to payment
      </button>
    </div>
  </div>
);

export default ReviewStep;
